

import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import ProgressSummary from './ProgressSummary';
import UploadItem from './UploadItem';
import { getUploadProgress, getItemUploadedSize } from '../utils';
import UploadPercent from './UploadPercent';
import { ITEM_STATUS } from '../../../constants';
import { uploadEvents } from '../../../utils/UploadEvents';

import { Icon, FloatingWindow } from '../../../../../ui-kit';
import '../styles/_upload-progress.scss';








function getHeaderTitle(itemsCount, errorCount) {
  const itemsString = `Uploading ${itemsCount} item${
  itemsCount > 1 ? 's' : ''
  }`;
  const errorString = errorCount ?
  ` with ${errorCount} error${errorCount === 1 ? '' : 's'}` :
  '';

  return itemsString + errorString;
}

function UploadProgress({ items, retry, remove, direction }) {
  // Add this state to track real-time progress data
  const [progressData, setProgressData] = useState({});
  // Listen for progress events to update our local progress data
  useEffect(() => {
    // Initialize tracking map with initial values
    const initialProgressData = {};
    (items || []).forEach((item) => {
      if (item.assetId) {
        initialProgressData[item.assetId] = {
          loaded: item.loaded || 0,
          totalSize: item.totalSize || 0,
          loadedPercent: item.loadedPercent || 0
        };
      }
    });
    setProgressData(initialProgressData);

    // Handle progress updates
    function onProgress({ assetId, loaded, totalSize, loadedPercent }) {
      setProgressData((prev) => ({
        ...prev,
        [assetId]: { loaded, totalSize, loadedPercent }
      }));
    }

    uploadEvents.on('progress', onProgress);
    return () => uploadEvents.off('progress', onProgress);
  }, [items]);

  // Calculate current progress data by combining props and event data
  const getEnhancedItems = () => (items || []).map((item) => {
    const progressUpdate = progressData[item.assetId];
    if (progressUpdate) {
      return {
        ...item,
        loaded: progressUpdate.loaded,
        totalSize: progressUpdate.totalSize,
        loadedPercent: progressUpdate.loadedPercent
      };
    }
    return item;
  });

  // Use enhanced items that include real-time progress
  const enhancedItems = getEnhancedItems();

  const getLoadedPercent = (item) => {
    const { loadedPercent, loaded, totalSize } = item;
    if (loadedPercent) {
      return loadedPercent;
    }
    return Math.floor(loaded / totalSize * 100);
  };

  const renderHeaderProgress = (isExpanded, currentItems = enhancedItems) => {
    const showBytesProgress = currentItems.length === 1 && !isExpanded;
    const totalProgress = showBytesProgress ?
    getUploadProgress(
      currentItems[0].loaded,
      currentItems[0].totalSize,
      currentItems[0].loadedPercent
    ) :

    <UploadPercent items={currentItems} />;

    return (
      <span
        className={cx({
          'expand-drag-container--header--info__percents__bytes-progress': showBytesProgress
        })}>

        {totalProgress}
      </span>);

  };

  const renderIcon = () => {
    if (direction === 'DOWN') return <Icon name="arrowdown" color="white" size={8} />;
    return null;
  };

  const renderHeader = React.useCallback(({ isExpanded }) => {
    const currentItems = getEnhancedItems();
    const loaded = getItemUploadedSize(currentItems[0]);
    const errors = currentItems.filter(({ uploadStatus }) => uploadStatus === 'error');
    return (
      <div className="expand-drag-container--header--custom">
        <div className="expand-drag-container--header--info">
          <div className="expand-drag-container--header--info__title">
            {getHeaderTitle(currentItems.length, errors.length)}
          </div>
          <div className="expand-drag-container--header--info__percents">
            {renderHeaderProgress(isExpanded, currentItems)}
          </div>
        </div>
        {!isExpanded && currentItems.length === 1 && loaded ?
        <ProgressSummary
          loadedPercent={getLoadedPercent(currentItems[0])}
          loaded={loaded}
          total={currentItems[0].totalSize}
          status={currentItems[0].uploadStatus}
          hideProgressValue /> :

        null}
      </div>);

  }, [progressData, items]);

  const renderItemsList = () =>
  <div>
      {enhancedItems.map((uploadItem) => {
      const {
        filename,
        loadedPercent,
        loaded,
        uploadStatus,
        totalSize,
        assetId,
        organisationId,
        metaData
      } = uploadItem;

      return (
        <UploadItem
          key={assetId}
          assetId={assetId}
          filename={filename}
          loadedPercent={loadedPercent}
          loaded={loaded}
          totalSize={totalSize}
          status={uploadStatus}
          retry={retry}
          remove={remove}
          organisationId={organisationId}
          metaData={metaData} />);


    })}
    </div>;


  if (!items || !items.length) {
    // WARNING - THIS WILL NOT COMPOSE NICELY WITH OTHER POSSIBLE FUTURE USES
    window.onbeforeunload = null;
    return null;
  }

  if (
  items.every(
    ({ uploadStatus }) => uploadStatus && uploadStatus === ITEM_STATUS.SUCCESS
  ))
  {
    // WARNING - THIS WILL NOT COMPOSE NICELY WITH OTHER POSSIBLE FUTURE USES
    window.onbeforeunload = null;
    return null;
  }

  return (
    <FloatingWindow
      header={renderHeader}
      content={renderItemsList()}
      classNames="uploader-container"
      toggleIcon={renderIcon()} />);


}

export default UploadProgress;